import { Form } from 'factorial-form'
import { pick } from 'lodash'
import { computed, makeObservable } from 'mobx'

import codes from 'modules/payroll_integrations/store/collections/codes'

interface CodeFormData {
  code: string | null
  codeable_id: number
  codeable_type: string
  codeable_label: string
  codeable_name: string
  integration: string
  forfait_jours: boolean
  position: number | null | undefined
}

type ReturnType = {
  code: string | null
  codeable_id: number
  codeable_type: string
  integration: string
  forfait_jours: boolean
  position: number | null | undefined
}

class CodeForm extends Form {
  constructor(code: Omit<CodeFormData, 'code'>) {
    const model = codes.find(
      (cod) =>
        cod.get('codeable_id') === code.codeable_id &&
        cod.get('codeable_type') === code.codeable_type &&
        cod.get('forfait_jours') === !!code.forfait_jours &&
        cod.get('integration') === code.integration
    )

    const obj = model
      ? {
          code: model.attributes.code,
          ...code,
        }
      : code
    super(obj, {
      code: 'string',
      codeable_id: 'number',
      codeable_type: 'string',
      codeable_label: 'string',
      codeable_name: 'string',
      integration: 'string',
      forfait_jours: 'boolean',
      position: 'number',
    })
    makeObservable(this, {
      element: computed,
    })
  }

  async submit() {
    const newModel = await this.isNew()
    if (newModel && this.containsValue()) {
      return this.create()
    } else {
      if (this.containsValue()) {
        return this.update()
      } else {
        return this.destroy()
      }
    }
  }

  async isNew() {
    const model = await this.getModel(this.element)
    return !model
  }

  async getModel(element: ReturnType) {
    await codes.fetch({
      data: {
        integration: element.integration,
        codeable_id: element.codeable_id,
        codeable_type: element.codeable_type,
        forfait_jours: element.forfait_jours,
      },
    })
    return codes.find(
      (code) =>
        code.get('codeable_id') === element.codeable_id &&
        code.get('codeable_type') === element.codeable_type &&
        code.get('forfait_jours') === !!element.forfait_jours &&
        code.get('integration') === element.integration
    )
  }

  containsValue() {
    return this.element.code !== null
  }

  async create() {
    await codes.create(this.element)
  }

  async update() {
    const model = await this.getModel(this.element)
    await model?.save({
      code: this.element.code,
    })
  }

  async destroy() {
    const model = await this.getModel(this.element)
    await model?.destroy()
    codes.remove(model?.attributes.id)
  }

  get element(): ReturnType {
    const data = this.data()

    return pick(data, [
      'code',
      'codeable_id',
      'codeable_type',
      'integration',
      'forfait_jours',
      'position',
    ])
  }
}

export default CodeForm
